import React from 'react';
import logo from './assets/general/compliance-graphic.png';

const Footer = ({ isArabic }) => {
  const companyName = {
    en: "Al Nakhla Exchange and Financial Services",
    ar: "شركة النخلة للصرافة والخدمات المالية"
  };

  const text = {
    en: {
      company: companyName.en,
      subtitle: "Trusted financial solutions aligned with the highest standards of compliance and transparency",
      links: {
        col1: ["About Us", "Our Vision", "Our Mission", "Services"],
        col2: ["Contact Us"],
        col3: [ "Terms of Service", "Compliance", "Governance"]

      },
      address: {
        street: "Al Ma'mun Street,",
        floor: "Tripoli,",
        building: "Libya",
        location: "",
        city: ""
      },
      copyright: `All Rights Reserved © ${companyName.en} - 2025`
    },
    ar: {
      company: companyName.ar,
      subtitle: "حلول مالية موثوقة… وفق أعلى معايير الامتثال والشفافية",
      links: {
        col1: ["من نحن", "رؤيتنا", "رسالتنا", "خدماتنا"],
        col2: ["تواصل معنا"],
        col3: ["الشروط والأحكام", "الامتثال", "الإدارة"]
      },
      address: {
        street: "شارع المأمون،",
        floor: "طرابلس،",
        building: "ليبيا",
        location: "",
        city: ""
      },
      copyright: `جميع الحقوق محفوظة – ${companyName.ar} – 2025`
    }
  };

  const currentLang = isArabic ? 'ar' : 'en';

  const linkHrefMap = {
    en: {
      "About Us": "#about",
      "Our Vision": "#vision",
      "Our Mission": "#vision",
      "Services": "#services",
      "Contact Us": "#contact",
      "Compliance": "#about",
      "Governance": "#about"
    },
    ar: {
      "من نحن": "#about",
      "رؤيتنا": "#vision",
      "رسالتنا": "#vision",
      "خدماتنا": "#services",
      "تواصل معنا": "#contact",
      "الامتثال": "#about",
      "الإدارة": "#about"
    }
  };

  const getLinksForColumn = (colKey) =>
    text[currentLang].links[colKey].filter(
      (label) => linkHrefMap[currentLang][label]
    );

  return (
    <footer className="py-6 sm:py-8" style={{backgroundColor: '#006655'}}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 max-w-7xl">
        {/* Logo and Social Media */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 sm:mb-12 gap-4">
          <div className="flex items-center">
            <img src={logo} alt="Al Nakhla Exchange" className="w-12 h-12 sm:w-16 sm:h-16 mr-3 sm:mr-4 flex-shrink-0" fetchPriority="high" />
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white">{text[currentLang].company}</h3>
              <p className="text-xs sm:text-sm text-gray-200">{text[currentLang].subtitle}</p>
            </div>
          </div>
          
          <div className="flex space-x-4">
            <a
              href="https://www.facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="#006655" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Footer Links */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-12">
          <div>
            {getLinksForColumn('col1').map((link, index) => (
              <div key={index} className="mb-2 sm:mb-3">
                <a
                  href={linkHrefMap[currentLang][link]}
                  className="text-sm sm:text-base text-gray-200 hover:text-white transition-colors"
                >
                  {link}
                </a>
              </div>
            ))}
          </div>
          
          <div>
            {getLinksForColumn('col2').map((link, index) => (
              <div key={index} className="mb-2 sm:mb-3">
                <a
                  href={linkHrefMap[currentLang][link]}
                  className="text-sm sm:text-base text-gray-200 hover:text-white transition-colors"
                >
                  {link}
                </a>
              </div>
            ))}
          </div>
          
          <div>
            {text[currentLang].links.col3.map((link, index) => (
              <div key={index} className="mb-2 sm:mb-3">
                <a
                  href={linkHrefMap[currentLang][link] || "#"}
                  className="text-sm sm:text-base text-gray-200 hover:text-white transition-colors"
                >
                  {link}
                </a>
              </div>
            ))}
          </div>
          
          <div>
            <div className="text-sm sm:text-base text-gray-200 space-y-1">
              <p>{text[currentLang].address.street}</p>
              <p>{text[currentLang].address.floor}</p>
              <p>{text[currentLang].address.building}</p>
              {text[currentLang].address.location && <p>{text[currentLang].address.location}</p>}
              {text[currentLang].address.city && <p>{text[currentLang].address.city}</p>}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-6 sm:pt-8 border-t border-gray-400">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs sm:text-sm text-gray-200 text-center sm:text-left">{text[currentLang].copyright}</p>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="w-10 h-10 rounded-full flex items-center justify-center bg-white hover:bg-gray-100 transition-colors">
              <svg className="w-5 h-5" fill="#006655" viewBox="0 0 24 24">
                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;